import React from 'react';
import PropTypes from 'prop-types';
import { GridToolbarContainer, useGridApiContext, gridSortedRowIdsSelector } from '@mui/x-data-grid';
import { Box } from '@mui/material';
import FileDownloadOutlinedIcon from '@mui/icons-material/FileDownloadOutlined';
import classNames from 'classnames';
import LoadingSpinner from 'src/component/UI/LoadingSpinner';
import variables from 'src/style/variable/variables.module.scss';
import { usePDFServices } from 'src/hooks/useReportData';

// Note: usePDFServices is a hook that wraps the async usePDFServices function from utils

interface ReportToolbarProps {
  onExport: (options: any) => void;
  onPdfDownload?: () => void;
  pdfLoading?: boolean;
  showPdf?: boolean;
  exportLabel: string;
  pdfLabel?: string;
}

/**
 * Reusable toolbar component for reports
 * Handles CSV export and PDF download with PDF services check
 */
export default function ReportToolbar ({
  onExport,
  onPdfDownload,
  pdfLoading = false,
  showPdf = false,
  exportLabel,
  pdfLabel
}: ReportToolbarProps) {
  const { usePDFServicesValue, usePDFServicesLoading } = usePDFServices();
  const apiRef = useGridApiContext();
  const getUnfilteredRows = ({ apiRef }) => gridSortedRowIdsSelector(apiRef);

  const displayedIcon = pdfLoading ? (
    <LoadingSpinner color={variables.primaryLight} />
  ) : (
    <FileDownloadOutlinedIcon className='custom-toolbar-download-icon' />
  );

  const handleExport = (options: any) => {
    if (apiRef && apiRef.current) {
      onExport({ ...options, apiRef });
    }
  };

  return (
    <GridToolbarContainer>
      <Box sx={{ flexGrow: 1 }} /> {/* Spacer to push the export icon to the right */}

      <div
        className='custom-toolbar-option'
        onClick={() => handleExport({ getRowsToExport: getUnfilteredRows })}
      >
        <FileDownloadOutlinedIcon className='custom-toolbar-download-icon' />
        <div className={classNames('custom-toolbar-text', 'custom-toolbar-text-reg ')}>
          {exportLabel}
        </div>
      </div>
      {!usePDFServicesLoading && usePDFServicesValue && showPdf && onPdfDownload && (
        <div className='custom-toolbar-option' onClick={onPdfDownload}>
          <div className={classNames('custom-toolbar-text', 'custom-toolbar-text-reg ')}>
            {displayedIcon}
            {pdfLabel}
          </div>
        </div>
      )}
    </GridToolbarContainer>
  );
}

ReportToolbar.propTypes = {
  onExport: PropTypes.func.isRequired,
  onPdfDownload: PropTypes.func,
  pdfLoading: PropTypes.bool,
  showPdf: PropTypes.bool,
  exportLabel: PropTypes.string.isRequired,
  pdfLabel: PropTypes.string
};
